import React, { useEffect, useRef } from 'react';
import SunEditor from 'suneditor-react';
import katex from 'katex';
import { en } from 'suneditor/src/lang';
import plugins from 'suneditor/src/plugins';

const defaultOptions = {
  previewTemplate: `
    <div style="width:auto; max-width:1136px; min-height:400px; margin:auto; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px;">
      {{contents}}
    </div>
  `,
  buttonList: [
    ['undo', 'redo'],
    ['font', 'fontSize', 'formatBlock'],
    ['paragraphStyle', 'blockquote'],
    ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
    ['fontColor', 'hiliteColor', 'textStyle'],
    ['removeFormat'],
    ['outdent', 'indent'],
    ['align', 'horizontalRule', 'list', 'lineHeight'],
    ['table', 'link', 'image', 'video'],
    ['fullScreen', 'showBlocks', 'codeView'],
    ['preview'],
    ['save'],
    [
      '%1161',
      [
        ['undo', 'redo'],
        [
          ':p-Formats-default.more_paragraph',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
        ],
        ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
        ['fontColor', 'hiliteColor', 'textStyle'],
        ['removeFormat'],
        ['outdent', 'indent'],
        ['align', 'horizontalRule', 'list', 'lineHeight'],
        [
          '-right',
          ':i-Etc-default.more_vertical',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
        ],
        [
          '-right',
          ':r-Table&Media-default.more_plus',
          'table',
          'link',
          'image',
          'video',
        ],
      ],
    ],
    [
      '%893',
      [
        ['undo', 'redo'],
        [
          ':p-Formats-default.more_paragraph',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
        ],
        ['bold', 'underline', 'italic', 'strike'],
        [
          ':t-Fonts-default.more_text',
          'subscript',
          'superscript',
          'fontColor',
          'hiliteColor',
          'textStyle',
        ],
        ['removeFormat'],
        ['outdent', 'indent'],
        ['align', 'horizontalRule', 'list', 'lineHeight'],
        [
          '-right',
          ':i-Etc-default.more_vertical',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
        ],
        [
          '-right',
          ':r-Table&Media-default.more_plus',
          'table',
          'link',
          'image',
          'video',
        ],
      ],
    ],
    [
      '%855',
      [
        ['undo', 'redo'],
        [
          ':p-Formats-default.more_paragraph',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
        ],
        [
          ':t-Fonts-default.more_text',
          'bold',
          'underline',
          'italic',
          'strike',
          'subscript',
          'superscript',
          'fontColor',
          'hiliteColor',
          'textStyle',
        ],
        ['removeFormat'],
        ['outdent', 'indent'],
        ['align', 'horizontalRule', 'list', 'lineHeight'],
        [':r-Table&Media-default.more_plus', 'table', 'link', 'image', 'video'],
        [
          '-right',
          ':i-Etc-default.more_vertical',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
        ],
      ],
    ],
    [
      '%563',
      [
        ['undo', 'redo'],
        [
          ':p-Formats-default.more_paragraph',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
        ],
        [
          ':t-Fonts-default.more_text',
          'bold',
          'underline',
          'italic',
          'strike',
          'subscript',
          'superscript',
          'fontColor',
          'hiliteColor',
          'textStyle',
        ],
        ['removeFormat'],
        ['outdent', 'indent'],
        [
          ':e-List&Line-default.more_horizontal',
          'align',
          'horizontalRule',
          'list',
          'lineHeight',
        ],
        [':r-Table&Media-default.more_plus', 'table', 'link', 'image', 'video'],
        [
          '-right',
          ':i-Etc-default.more_vertical',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
        ],
      ],
    ],
    [
      '%458',
      [
        ['undo', 'redo'],
        [
          ':p-Formats-default.more_paragraph',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
        ],
        [
          ':t-Fonts-default.more_text',
          'bold',
          'underline',
          'italic',
          'strike',
          'subscript',
          'superscript',
          'fontColor',
          'hiliteColor',
          'textStyle',
          'removeFormat',
        ],
        [
          ':e-List&Line-default.more_horizontal',
          'outdent',
          'indent',
          'align',
          'horizontalRule',
          'list',
          'lineHeight',
        ],
        [':r-Table&Media-default.more_plus', 'table', 'link', 'image', 'video'],
        [
          '-right',
          ':i-Etc-default.more_vertical',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
        ],
      ],
    ],
  ],
  attributesWhitelist: {
    all: 'style|data-.+',
    input: 'checked|name',
    '???': '*',
  },
  defaultStyle:
    "font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; color: #111827; line-height: 1.6;",
  plugins: plugins,
  lang: en,
  stickyToolbar: 0,
  katex: katex,
  width: '100%',
  height: 'auto',
  minHeight: '200px',
  imageMultipleFile: true,
  strictMode: false,
  iframe: false,
};

const SunEditorComponent = ({
  value,
  onChange,
  options = {},
  justUpdatedValue,
}) => {
  const editorRef = useRef(null);
  const wrapperRef = useRef(null);
  // Loading a value makes the editor re-format it (and fire onChange with its
  // own HTML), which callers would mistake for a user edit. baselineRef is
  // the editor's version of the value it was given; while the content still
  // equals it, the original value is reported instead.
  const initialValueRef = useRef(value);
  const baselineRef = useRef(null);
  const loadingRef = useRef(false);

  const handleChange = (content) => {
    if (!onChange) return;
    if (loadingRef.current || content === baselineRef.current) {
      onChange(initialValueRef.current ?? content);
      return;
    }
    onChange(content);
  };

  // Strip native browser title tooltips from all toolbar buttons
  // so only SunEditor's own .se-tooltip-inner shows — no double tooltip
  const stripNativeTooltips = () => {
    if (!wrapperRef.current) return;
    wrapperRef.current
      .querySelectorAll('.se-toolbar button[title], .se-toolbar [title]')
      .forEach((el) => {
        el.setAttribute('data-se-title', el.getAttribute('title'));
        el.removeAttribute('title');
      });
  };

  useEffect(() => {
    if (editorRef.current) {
      try {
        loadingRef.current = true;
        if (value !== editorRef.current.getContents()) {
          editorRef.current.setContents(value || '');
        }
        baselineRef.current = editorRef.current.getContents();
      } catch (e) {
        // iframe not ready yet
      } finally {
        loadingRef.current = false;
      }
    }
    // Small delay so the toolbar is fully rendered before we strip titles
    const t = setTimeout(stripNativeTooltips, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (editorRef.current && typeof justUpdatedValue === 'string') {
      editorRef.current.setContents(justUpdatedValue);
    }
  }, [justUpdatedValue]);

  const stringifiedOptions = JSON.stringify(options);
  const mergedOptions = React.useMemo(() => {
    return { ...defaultOptions, ...JSON.parse(stringifiedOptions) };
  }, [stringifiedOptions]);

  return (
    <div className="sun-editor-wrapper" ref={wrapperRef}>
      <style>{`
        /* ─── Container ─────────────────────────────────── */
        .sun-editor-wrapper {
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid rgba(63, 63, 70, 0.5); /* border-zinc-700/50 */
          background: rgba(24, 24, 27, 0.4); /* bg-zinc-900/40 */
          box-shadow: 0 1px 3px rgba(0,0,0,0.07);
        }
        .sun-editor-wrapper .sun-editor {
          border: none !important;
          background: transparent !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        /* ─── Toolbar ────────────────────────────────────── */
        .sun-editor-wrapper .se-toolbar {
          background: transparent !important;
          border-bottom: 1px solid rgba(63, 63, 70, 0.2) !important;
          border-top: none !important;
          outline: none !important;
          padding: 4px 6px !important;
          box-shadow: none !important;
          line-height: 1 !important;
        }
        .sun-editor-wrapper .se-btn-module {
          border-right: 1px solid rgba(63, 63, 70, 0.2) !important;
          margin-right: 2px !important;
          padding-right: 2px !important;
        }
        .sun-editor-wrapper .se-btn-module:last-child {
          border-right: none !important;
        }

        /* ─── Toolbar buttons ────────────────────────────── */
        .sun-editor-wrapper .se-btn,
        .sun-editor-wrapper .se-toolbar button {
          width: 28px !important;
          height: 28px !important;
          min-width: 28px !important;
          border-radius: 5px !important;
          border: none !important;
          background: transparent !important;
          color: #a1a1aa !important; /* text-zinc-400 */
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 0 !important;
          margin: 1px !important;
          cursor: pointer !important;
          transition: background 0.1s, color 0.1s !important;
          font-size: 11px !important;
          line-height: 1 !important;
          vertical-align: middle !important;
        }
        .sun-editor-wrapper .se-btn:hover,
        .sun-editor-wrapper .se-toolbar button:hover {
          background: rgba(63, 63, 70, 0.2) !important; /* hover:bg-zinc-700/50 */
          color: #e4e4e7 !important; /* text-zinc-200 */
        }
        .sun-editor-wrapper .se-btn.active,
        .sun-editor-wrapper .se-toolbar button.active {
          background: rgba(63, 63, 70, 0.8) !important;
          color: #fff !important;
        }

        /* ─── SVG icons — solid/filled ──────────────────── */
        .sun-editor-wrapper .se-btn svg,
        .sun-editor-wrapper .se-toolbar button svg,
        .sun-editor-wrapper .se-btn svg *,
        .sun-editor-wrapper .se-toolbar button svg * {
          width: 14px !important;
          height: 14px !important;
          fill: #a1a1aa !important; /* text-zinc-400 */
          stroke: none !important;
          stroke-width: 0 !important;
          pointer-events: none !important;
        }
        /* Root svg size must be set on the element itself */
        .sun-editor-wrapper .se-btn svg,
        .sun-editor-wrapper .se-toolbar button svg {
          width: 14px !important;
          height: 14px !important;
        }
        .sun-editor-wrapper .se-btn:hover svg *,
        .sun-editor-wrapper .se-toolbar button:hover svg * {
          fill: #e4e4e7 !important; /* text-zinc-200 */
          stroke: none !important;
        }
        .sun-editor-wrapper .se-btn.active svg *,
        .sun-editor-wrapper .se-toolbar button.active svg * {
          fill: #fff !important;
          stroke: none !important;
        }
        /* Explicitly kill any stroke that SunEditor sets inline */
        .sun-editor-wrapper .se-btn svg path,
        .sun-editor-wrapper .se-btn svg rect,
        .sun-editor-wrapper .se-btn svg circle,
        .sun-editor-wrapper .se-btn svg line,
        .sun-editor-wrapper .se-btn svg polyline,
        .sun-editor-wrapper .se-btn svg polygon {
          fill: inherit !important;
          stroke: none !important;
        }

        /* ─── TOOLTIP — Kill the default, keep ONE ───────── */
        /*
          SunEditor wraps each button in a .se-tooltip span.
          The ::after / ::before pseudo-elements on .se-tooltip are
          the "arrow" and "bubble" of the NATIVE se-tooltip.
          We hide ALL of those and instead style the .se-tooltip-inner
          element that SunEditor inserts as a real DOM node.
        */

        /* 1. Hide SunEditor's CSS-only pseudo-element tooltip */
        .sun-editor-wrapper .se-tooltip::after,
        .sun-editor-wrapper .se-tooltip::before {
          display: none !important;
          content: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }

        /* 2. Also hide the wrapper that animates the pseudo tooltip */
        .sun-editor-wrapper .se-tooltip-inner {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }

        /* 3. Style the REAL tooltip element SunEditor injects as sibling */
        .sun-editor-wrapper .se-tooltip-content {
          position: absolute !important;
          z-index: 9999 !important;
          bottom: calc(100% + 6px) !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          background: #18181b !important; /* bg-zinc-900 */
          color: #e4e4e7 !important; /* text-zinc-200 */
          font-size: 11px !important;
          font-weight: 500 !important;
          line-height: 1.3 !important;
          padding: 4px 8px !important;
          border-radius: 5px !important;
          white-space: nowrap !important;
          pointer-events: none !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.25) !important;
          border: 1px solid rgba(63, 63, 70, 0.2) !important;
        }

        /* ─── Select dropdowns (Font, Size, Format) ─────── */
        .sun-editor-wrapper .se-btn-select {
          height: 28px !important;
          border-radius: 5px !important;
          border: 1px solid rgba(63, 63, 70, 0.2) !important;
          background: transparent !important;
          color: #a1a1aa !important;
          font-size: 11px !important;
          padding: 0 6px !important;
          white-space: nowrap !important;
        }
        .sun-editor-wrapper .se-btn-select:hover {
          background: rgba(63, 63, 70, 0.2) !important;
        }
        .sun-editor-wrapper .se-btn-select .txt {
          color: #a1a1aa !important;
          font-size: 11px !important;
        }

        /* ─── Dropdown list panels ───────────────────────── */
        .sun-editor-wrapper .se-list-layer,
        .sun-editor-wrapper .se-selector-font,
        .sun-editor-wrapper .se-selector-font-size {
          background: #18181b !important; /* bg-zinc-900 */
          border: 1px solid rgba(63, 63, 70, 0.2) !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 14px rgba(0,0,0,0.50) !important;
          padding: 4px !important;
          z-index: 10000 !important;
        }
        .sun-editor-wrapper .se-list-layer li button,
        .sun-editor-wrapper .se-list-inner li button {
          color: #a1a1aa !important;
          font-size: 12px !important;
          border-radius: 4px !important;
          padding: 5px 8px !important;
          width: 100% !important;
          text-align: left !important;
        }
        .sun-editor-wrapper .se-list-layer li button:hover,
        .sun-editor-wrapper .se-list-inner li button:hover {
          background: rgba(63, 63, 70, 0.2) !important;
          color: #e4e4e7 !important;
        }
        .sun-editor-wrapper .se-list-layer li.active button {
          background: rgba(63, 63, 70, 0.8) !important;
          color: #fff !important;
        }

        /* ─── Writing area ───────────────────────────────── */
        .sun-editor-wrapper .se-wrapper,
        .sun-editor-wrapper .se-wrapper-inner {
          background: transparent !important;
        }
        .sun-editor-wrapper .sun-editor-editable {
          background: transparent !important;
          color: #d4d4d8 !important; /* text-zinc-300 */
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
          font-size: 14px !important;
          line-height: 1.65 !important;
          padding: 16px 20px !important;
          caret-color: #d4d4d8 !important;
        }
        .sun-editor-wrapper .sun-editor-editable:empty:before {
          color: #52525b !important; /* placeholder text-zinc-500 */
        }
        
        /* Hide placeholder when editor is focused */
        .sun-editor-wrapper .se-wrapper:focus-within .se-placeholder {
          display: none !important;
        }

        /* ─── Code view ──────────────────────────────────── */
        .sun-editor-wrapper .se-wrapper-code {
          background: #09090b !important; /* bg-zinc-950 */
          color: #d4d4d8 !important;
          font-family: 'Fira Code', 'Cascadia Code', Consolas, monospace !important;
          font-size: 13px !important;
          padding: 16px !important;
          border-top: 1px solid rgba(63, 63, 70, 0.2) !important;
        }

        /* ─── Resize bar ─────────────────────────────────── */
        .sun-editor-wrapper .se-resizing-bar {
          background: transparent !important;
          border-top: 1px solid rgba(63, 63, 70, 0.2) !important;
          height: 8px !important;
        }
        .sun-editor-wrapper .se-resizing-bar .se-navigation {
          color: #52525b !important; /* text-zinc-500 */
          font-size: 11px !important;
        }

        /* ─── Link / Image dialogs ───────────────────────── */
        .sun-editor-wrapper .se-dialog-inner {
          background: #18181b !important; /* bg-zinc-900 */
          border-radius: 10px !important;
          border: 1px solid rgba(63, 63, 70, 0.2) !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
          overflow: hidden !important;
        }
        .sun-editor-wrapper .se-dialog-title {
          background: transparent !important;
          border-bottom: 1px solid rgba(63, 63, 70, 0.2) !important;
          color: #e4e4e7 !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          padding: 12px 16px !important;
        }
        .sun-editor-wrapper .se-dialog-body {
          padding: 16px !important;
          color: #a1a1aa !important;
        }
        .sun-editor-wrapper .se-dialog-form label {
          font-size: 12px !important;
          color: #a1a1aa !important;
          font-weight: 500 !important;
          margin-bottom: 4px !important;
          display: block !important;
        }
        .sun-editor-wrapper .se-dialog-form input,
        .sun-editor-wrapper .se-dialog-form select,
        .sun-editor-wrapper .se-dialog-form textarea {
          border: 1px solid rgba(63, 63, 70, 0.2) !important;
          border-radius: 6px !important;
          background: rgba(39, 39, 42, 0.5) !important; /* bg-zinc-800 */
          color: #e4e4e7 !important;
          font-size: 13px !important;
          padding: 6px 10px !important;
          width: 100% !important;
          outline: none !important;
        }
        .sun-editor-wrapper .se-dialog-form input:focus,
        .sun-editor-wrapper .se-dialog-form select:focus {
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 2px rgba(99,102,241,0.15) !important;
        }
        .sun-editor-wrapper .se-dialog-footer {
          background: transparent !important;
          border-top: 1px solid rgba(63, 63, 70, 0.2) !important;
          padding: 10px 16px !important;
          display: flex !important;
          gap: 8px !important;
          justify-content: flex-end !important;
        }
        .sun-editor-wrapper .se-btn-primary,
        .sun-editor-wrapper .se-dialog button.se-btn-primary {
          background: #4f46e5 !important; /* Indigo 600 */
          color: #fff !important;
          border: none !important;
          border-radius: 6px !important;
          padding: 6px 14px !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          cursor: pointer !important;
        }
        .sun-editor-wrapper .se-btn-primary:hover,
        .sun-editor-wrapper .se-dialog button.se-btn-primary:hover {
          background: #4338ca !important; /* Indigo 700 */
        }

        /* ─── Table in editable area ─────────────────────── */
        .sun-editor-wrapper .sun-editor-editable table {
          border-collapse: collapse !important;
          width: 100% !important;
        }
        .sun-editor-wrapper .sun-editor-editable td,
        .sun-editor-wrapper .sun-editor-editable th {
          border: 1px solid rgba(63, 63, 70, 0.2) !important;
          padding: 8px 12px !important;
        }
        .sun-editor-wrapper .sun-editor-editable th {
          background: rgba(39, 39, 42, 0.5) !important;
          font-weight: 600 !important;
          color: #e4e4e7 !important;
        }

        /* ─── Blockquote ─────────────────────────────────── */
        .sun-editor-wrapper .sun-editor-editable blockquote {
          border-left: 3px solid #6366f1 !important;
          margin: 8px 0 !important;
          padding: 4px 12px !important;
          color: #a1a1aa !important;
          background: rgba(39, 39, 42, 0.3) !important;
        }

        /* ─── More-group collapsed button ────────────────── */
        .sun-editor-wrapper .se-btn-module-border {
          border: 1px solid rgba(63, 63, 70, 0.2) !important;
          border-radius: 6px !important;
          background: transparent !important;
        }
        .sun-editor-wrapper .se-btn-module-border:hover {
          background: rgba(63, 63, 70, 0.2) !important;
        }

        /* ─── Scrollbar inside list panels ───────────────── */
        .sun-editor-wrapper .se-list-inner::-webkit-scrollbar { width: 4px; }
        .sun-editor-wrapper .se-list-inner::-webkit-scrollbar-thumb {
          background: #52525b; /* zinc-600 */
          border-radius: 4px;
        }

        /* ─── More Button Tray (3 vertical dots) ─────────── */
        .sun-editor-wrapper .se-btn-tray,
        .sun-editor-wrapper .se-toolbar-tray,
        .sun-editor-wrapper .se-menu-tray {
          background: #18181b !important; /* bg-zinc-900 */
          border-top: 1px solid rgba(63, 63, 70, 0.2) !important;
        }
        .sun-editor-wrapper .se-toolbar-more-layer {
          background: #18181b !important; /* bg-zinc-900 */
          border-top: none !important;
        }
        .sun-editor-wrapper .se-toolbar-more-layer .se-more-layer {
          border-top: none !important;
        }
      `}</style>

      <SunEditor
        getSunEditorInstance={(editor) => (editorRef.current = editor)}
        setOptions={mergedOptions}
        onChange={handleChange}
        placeholder="Write you content here..."
      />
    </div>
  );
};

export default SunEditorComponent;
